/**
 * Model identity: stable URL slugs, cross-provider grouping keys and display names.
 *
 * The browser app, the build scripts (prerender, sitemap, agent assets) and the
 * Worker all import this module so that a model resolves to the same URL and the
 * same name everywhere.
 */
/** Namespaced provider IDs such as Cloudflare Workers AI `@cf/meta/llama-3.1-8b-instruct-fp8`. */
const NAMESPACED_MODEL_ID = /^@[a-z0-9-]+\/[^/]+\/(.+)$/i;
/**
 * Serving/packaging variants. They describe how an inference provider runs the
 * weights, not a different model, so they are kept as offering attributes and
 * excluded from the grouping key.
 */
const VARIANT_TOKENS = {
    fp8: 'FP8',
    fp16: 'FP16',
    bf16: 'BF16',
    int8: 'INT8',
    int4: 'INT4',
    awq: 'AWQ',
    gptq: 'GPTQ',
    maas: 'MaaS',
    hf: 'HF',
};
/**
 * Trailing serving modifiers that only count as a variant after a quantisation
 * token (`-fp8-fast`). Elsewhere "fast" names a distinct model tier
 * (`imagen-4.0-fast-generate-001`).
 */
const QUANTISATION_TOKENS = new Set(['fp8', 'fp16', 'bf16', 'int8', 'int4', 'awq', 'gptq']);
function isVariantTokenAt(tokens, index) {
    const token = tokens[index];
    if (token in VARIANT_TOKENS)
        return true;
    return (token === 'fast' && index === tokens.length - 1 && QUANTISATION_TOKENS.has(tokens[index - 1]));
}
/** Publisher names that refer to the same organisation. */
const PUBLISHER_ALIASES = {
    zai: 'zhipuai',
    zaiorg: 'zhipuai',
    zhipu: 'zhipuai',
    mistral: 'mistralai',
    metallama: 'meta',
    moonshotai: 'moonshot',
    deepseekai: 'deepseek',
    alibaba: 'qwen',
};
const WORD_CASING = {
    ai: 'AI',
    api: 'API',
    asr: 'ASR',
    automl: 'AutoML',
    bart: 'BART',
    bert: 'BERT',
    bge: 'BGE',
    chatgpt: 'ChatGPT',
    cnn: 'CNN',
    codegemma: 'CodeGemma',
    codellama: 'Code Llama',
    cxr: 'CXR',
    dbrx: 'DBRX',
    deepseek: 'DeepSeek',
    distilbert: 'DistilBERT',
    dreamshaper: 'DreamShaper',
    embeddinggemma: 'EmbeddingGemma',
    en: 'EN',
    es: 'ES',
    eurollm: 'EuroLLM',
    flux: 'FLUX',
    functiongemma: 'FunctionGemma',
    glm: 'GLM',
    gpt: 'GPT',
    hd: 'HD',
    imagegeneration: 'Image Generation',
    indictrans2: 'IndicTrans2',
    it: 'IT',
    lcm: 'LCM',
    lion: 'LION',
    llm: 'LLM',
    llava: 'LLaVA',
    lora: 'LoRA',
    m2m100: 'M2M100',
    medgemma: 'MedGemma',
    melotts: 'MeloTTS',
    minimax: 'MiniMax',
    moe: 'MoE',
    multimodalembedding: 'Multimodal Embedding',
    ocr: 'OCR',
    openai: 'OpenAI',
    oss: 'OSS',
    paligemma: 'PaliGemma',
    phonellm: 'PhoneLLM',
    plamo: 'PLaMo',
    qwq: 'QwQ',
    resnet: 'ResNet',
    resnet50: 'ResNet50',
    sdxl: 'SDXL',
    sea: 'SEA',
    shieldgemma: 'ShieldGemma',
    shieldgemma2: 'ShieldGemma 2',
    sst: 'SST',
    t5gemma: 'T5Gemma',
    textembedding: 'Text Embedding',
    translategemma: 'TranslateGemma',
    tts: 'TTS',
    txgemma: 'TxGemma',
    vit: 'ViT',
    vl: 'VL',
    xl: 'XL',
    yolo: 'YOLO',
    ...VARIANT_TOKENS,
    fast: 'Fast',
};
/** Families whose public names separate the family from the version ("Llama 3.1"). */
const SPACED_VERSION_FAMILIES = /^(llama|gemma|claude|phi|granite|nemotron)(\d.*)$/;
/**
 * Normalise a provider model ID to its model name by removing account scopes,
 * provider namespaces, routing/vendor prefixes and release-date/revision suffixes.
 *
 *   accounts/fireworks/models/deepseek-v4-pro      → deepseek-v4-pro
 *   @cf/meta/llama-3.1-8b-instruct-fp8              → llama-3.1-8b-instruct-fp8
 *   eu.anthropic.claude-sonnet-4-5-20250929-v1:0    → claude-sonnet-4-5
 *   gpt-4o-2024-08-06                               → gpt-4o
 */
export function normalizeModelId(modelId) {
    let id = String(modelId || '').trim();
    id = id.replace(/^accounts\/[^/]+\/models\//, '');
    const namespaced = NAMESPACED_MODEL_ID.exec(id);
    if (namespaced)
        id = namespaced[1];
    // Routing/vendor prefixes ("us.", "anthropic.", "ai21."), at most two. A
    // prefix is followed by a letter, so dotted versions ("qwen3.5", "gpt-4.1")
    // are kept.
    for (let i = 0; i < 2; i++) {
        id = id.replace(/^[a-z][a-z0-9]*(?:-[a-z]+)*\.(?=[a-z])/, '');
    }
    return id
        .replace(/:\d+$/, '')
        .replace(/-v\d+$/, '')
        .replace(/-\d{8,}(-v\d+)?$/, '')
        .replace(/-\d{4}-\d{2}-\d{2}$/, '');
}
/** Restrict a slug to URL-safe characters without a path separator. */
function sanitizeSlug(value) {
    return value
        .toLowerCase()
        .replace(/[^a-z0-9._-]+/g, '-')
        .replace(/-{2,}/g, '-')
        .replace(/^[-.]+|[-.]+$/g, '');
}
/**
 * The slug for a single provider model ID. It never contains `/`, `@` or other
 * characters that need percent-encoding.
 *
 *   claude-sonnet-4-5                    → claude-sonnet-4.5
 *   @cf/meta/llama-3.1-8b-instruct-fp8   → llama-3.1-8b-instruct-fp8
 */
export function generateModelSlug(modelId) {
    const slug = normalizeModelId(modelId)
        // A split minor version (claude-sonnet-4-5 → 4.5), but not a snapshot
        // number (gpt-4-0613) or a month-year pair (preview-09-2025).
        .replace(/(^|[^0-9])(\d{1,2})-(\d{1,2})$/, '$1$2.$3')
        .replace(/-v\d+$/, '')
        .replace(/-\d{8}(-v\d+)?$/, '')
        .replace(/-\d{4}-\d{2}-\d{2}$/, '');
    return sanitizeSlug(slug);
}
/**
 * Slugs that earlier releases published for this model ID. They must keep
 * resolving (and redirect) to the current slug.
 */
export function legacyModelSlugs(modelId) {
    const legacyNormalized = String(modelId || '')
        .replace(/^accounts\/[^/]+\/models\//, '')
        .replace(/^(?:[a-z]+(?:-[a-z]+)*\.(?=[a-z])){0,2}/, '')
        .replace(/-v\d+$/, '')
        .replace(/-\d{8,}(-v\d+)?$/, '')
        .replace(/-\d{4}-\d{2}-\d{2}$/, '');
    const legacy = legacyNormalized
        .replace(/(\d+)-(\d+)$/, '$1.$2')
        .replace(/-v\d+$/, '')
        .replace(/-\d{8}(-v\d+)?$/, '')
        .replace(/-\d{4}-\d{2}-\d{2}$/, '');
    const current = generateModelSlug(modelId);
    return Array.from(new Set([legacy, normalizeModelId(modelId)])).filter((slug) => slug && slug !== current);
}
/** True when a `/models/:slug` path segment is not already in canonical slug form. */
export function isLegacyModelPathSegment(segment) {
    return sanitizeSlug(segment) !== segment;
}
/** Map a raw path segment (slug, legacy slug or provider model ID) to slug form. */
export function modelPathSegmentToSlug(segment) {
    if (!isLegacyModelPathSegment(segment))
        return segment;
    return generateModelSlug(segment);
}
export function normalizePublisherKey(publisher) {
    const compact = String(publisher || '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '');
    return PUBLISHER_ALIASES[compact] ?? compact;
}
function splitIdentityTokens(modelId) {
    return normalizeModelId(modelId)
        .toLowerCase()
        .replace(/(\d)p(\d)/g, '$1.$2')
        .split(/[-_/\s]+/)
        .filter(Boolean);
}
/** Serving variants carried by a model ID, e.g. `["FP8", "Fast"]`. */
export function modelVariantTags(modelId) {
    const tokens = splitIdentityTokens(modelId);
    return tokens
        .filter((token, index) => isVariantTokenAt(tokens, index) && token !== 'maas' && token !== 'hf')
        .map((token) => VARIANT_TOKENS[token] ?? 'Fast');
}
/**
 * Cross-provider grouping key: publisher + family + version + size + tuning.
 * Quantisation and serving variants are ignored; size, tuning (Instruct, IT, base),
 * LoRA support and release revisions are kept so different models never merge.
 *
 *   ("Meta", "@cf/meta/llama-3.1-8b-instruct-fp8")  → meta::llama-3-1-8-b-instruct
 *   ("Meta", "meta.llama3-1-8b-instruct-v1:0")      → meta::llama-3-1-8-b-instruct
 */
export function canonicalModelKey(modelId, publisher) {
    const tokens = splitIdentityTokens(modelId);
    const parts = tokens
        .filter((_, index) => !isVariantTokenAt(tokens, index))
        .flatMap((token) => token.split('.'))
        .flatMap((token) => token.match(/[a-z]+|\d+/g) ?? []);
    return `${normalizePublisherKey(publisher)}::${parts.join('-')}`;
}
function formatToken(token) {
    const lower = token.toLowerCase();
    if (WORD_CASING[lower])
        return WORD_CASING[lower];
    // Parameter counts and mixture-of-experts shapes: 8b, 1.2b, 300m, 16e, a4b, 8x7b.
    if (/^\d+(?:\.\d+)?[bmkte]$/.test(lower))
        return lower.toUpperCase();
    if (/^a\d+(?:\.\d+)?b$/.test(lower))
        return lower.toUpperCase();
    if (/^\d+x\d+(?:\.\d+)?b$/.test(lower))
        return lower.replace(/b$/, 'B');
    // Version tags: v4 → V4, v1.5 → V1.5.
    if (/^v\d+(?:\.\d+)*$/.test(lower))
        return lower.toUpperCase();
    // OpenAI reasoning series keep their lowercase brand (o1, o3, o4).
    if (/^o\d/.test(lower))
        return lower;
    // Stable Diffusion short names: sd3.5 → SD3.5.
    if (/^sd\d/.test(lower))
        return lower.toUpperCase();
    const spaced = SPACED_VERSION_FAMILIES.exec(lower);
    if (spaced)
        return `${formatToken(spaced[1])} ${spaced[2]}`;
    return lower.charAt(0).toUpperCase() + lower.slice(1);
}
/**
 * Human-readable model name from a provider model ID.
 *
 *   @cf/meta/llama-3.1-8b-instruct-fp8          → Llama 3.1 8B Instruct FP8
 *   meta.llama3-1-8b-instruct-v1:0              → Llama 3.1 8B Instruct
 *   eu.anthropic.claude-sonnet-4-5-20250929-v1  → Claude Sonnet 4.5
 *   @cf/aisingapore/gemma-sea-lion-v4-27b-it    → Gemma Sea Lion V4 27B IT
 */
export function formatModelIdDisplayName(modelId, publisher) {
    const tokens = normalizeModelId(modelId)
        // Vendor prefixes that contain digits ("ai21.") are not stripped from slugs.
        .replace(/^[a-z][a-z0-9]*\.(?=[a-z])/, '')
        .replace(/(^|[-_])([a-z]*\d+)p(\d+)(?=$|[-_])/gi, '$1$2.$3')
        .split(/[-_\s]+/)
        .filter(Boolean);
    // Join split version numbers (claude-3-5 → 3.5, llama3-1 → llama3.1), but never
    // a version with a size or a date (llama-3.1-8b stays "3.1 8B").
    const merged = [];
    for (const token of tokens) {
        const previous = merged[merged.length - 1];
        if (previous !== undefined &&
            /^\d{1,2}$/.test(token) &&
            /\d$/.test(previous) &&
            !previous.includes('.') &&
            !/^\d{3,}$/.test(previous)) {
            merged[merged.length - 1] = `${previous}.${token}`;
        }
        else {
            merged.push(token);
        }
    }
    const name = merged.map(formatToken).join(' ');
    // IDs that are only a version ("deepseek.v3.1" → "v3.1", "r1") need the publisher.
    if (publisher && /^(?:v\d|r\d)/i.test(merged[0] ?? '')) {
        return `${publisher} ${name}`;
    }
    return name;
}
/**
 * True when a description is the collector's generic template rather than
 * provider- or editor-supplied copy.
 */
export function isGeneratedModelDescription(description) {
    if (!description)
        return true;
    const text = description.trim();
    return (text.length === 0 ||
        /@[a-z0-9-]+\//i.test(text) ||
        /delivers reliable performance for (?:standard AI workloads|a wide range of text-based tasks)/i.test(text) ||
        /is a foundation model with general-purpose capabilities\.$/i.test(text) ||
        /^This .+ model offers balanced capabilities for diverse applications\.$/i.test(text) ||
        / is an open-source model from Meta with strong community support\.$/i.test(text) ||
        / open-source model enables customization and fine-tuning\.$/i.test(text) ||
        /provides flexible deployment options with open weights\.$/i.test(text));
}
/** Truncate text for meta descriptions at a word boundary, adding an ellipsis. */
export function truncateDescription(text, maxLength = 160) {
    const clean = String(text || '')
        .replace(/\s+/g, ' ')
        .trim();
    if (clean.length <= maxLength)
        return clean;
    const slice = clean.slice(0, maxLength - 1);
    const boundary = slice.lastIndexOf(' ');
    const cut = boundary > maxLength * 0.6 ? slice.slice(0, boundary) : slice;
    return `${cut.replace(/[\s,;:.–-]+$/, '')}…`;
}
/** `pluralize(1, 'region')` → "1 region"; `pluralize(3, 'delta')` → "3 deltas". */
export function pluralize(count, singular, plural = `${singular}s`) {
    return `${count.toLocaleString('en-US')} ${count === 1 ? singular : plural}`;
}
/** The noun alone, for labels that render the number separately. */
export function pluralNoun(count, singular, plural = `${singular}s`) {
    return count === 1 ? singular : plural;
}
/** The model name without serving-variant tokens (FP8, Fast, MaaS). */
export function withoutVariantTokens(modelId) {
    const tokens = normalizeModelId(modelId).split(/[-_]/).filter(Boolean);
    const lower = tokens.map((token) => token.toLowerCase());
    const kept = tokens.filter((_, index) => !isVariantTokenAt(lower, index));
    return kept.length > 0 ? kept.join('-') : normalizeModelId(modelId);
}
function hasVariant(modelId) {
    const tokens = splitIdentityTokens(modelId);
    return tokens.some((_, index) => isVariantTokenAt(tokens, index));
}
/**
 * Group provider model IDs into canonical models and assign each a unique slug.
 * The output depends only on the set of inputs, not their order.
 */
export function buildModelIdentityIndex(inputs) {
    const membersByKey = new Map();
    for (const input of inputs) {
        if (!input.modelId)
            continue;
        const key = canonicalModelKey(input.modelId, input.publisher);
        const members = membersByKey.get(key) ?? new Map();
        const existing = members.get(input.modelId);
        if (existing) {
            existing.weight += input.weight ?? 1;
        }
        else {
            members.set(input.modelId, {
                modelId: input.modelId,
                publisher: input.publisher,
                weight: input.weight ?? 1,
                slug: generateModelSlug(input.modelId),
            });
        }
        membersByKey.set(key, members);
    }
    // Aggregate weight per candidate slug, then rank: plain IDs before serving
    // variants, then most deployments, shortest, alphabetical.
    const preferred = (members) => {
        const slugWeight = new Map();
        for (const member of members) {
            slugWeight.set(member.slug, (slugWeight.get(member.slug) ?? 0) + member.weight);
        }
        return [...members].sort((a, b) => Number(hasVariant(a.modelId)) - Number(hasVariant(b.modelId)) ||
            (slugWeight.get(b.slug) ?? 0) - (slugWeight.get(a.slug) ?? 0) ||
            a.slug.length - b.slug.length ||
            a.slug.localeCompare(b.slug) ||
            a.modelId.localeCompare(b.modelId))[0];
    };
    const groups = [];
    const usedSlugs = new Set();
    const sortedKeys = [...membersByKey.keys()].sort((a, b) => {
        const weight = (key) => [...membersByKey.get(key).values()].reduce((sum, member) => sum + member.weight, 0);
        return weight(b) - weight(a) || a.localeCompare(b);
    });
    for (const key of sortedKeys) {
        const members = [...membersByKey.get(key).values()];
        const lead = preferred(members);
        let slug = lead.slug || sanitizeSlug(key.replace('::', '-'));
        // IDs that are only a version ("deepseek.r1", "deepseek.v3.1") need the
        // publisher to make a meaningful slug: deepseek-r1, deepseek-v3.1.
        if (/^[vr]\d/.test(slug) && sanitizeSlug(lead.publisher)) {
            slug = `${sanitizeSlug(lead.publisher)}-${slug}`;
        }
        if (usedSlugs.has(slug)) {
            const publisherSlug = sanitizeSlug(lead.publisher) || 'model';
            slug = `${publisherSlug}-${slug}`;
            let suffix = 2;
            while (usedSlugs.has(slug))
                slug = `${publisherSlug}-${lead.slug}-${suffix++}`;
        }
        usedSlugs.add(slug);
        const variants = Array.from(new Set(members.flatMap((m) => modelVariantTags(m.modelId))));
        const modelIds = members.map((m) => m.modelId).sort();
        groups.push({
            key,
            slug,
            publisher: lead.publisher,
            displayName: formatModelIdDisplayName(withoutVariantTokens(lead.modelId), lead.publisher),
            modelIds,
            aliases: [],
            variants,
        });
    }
    const byKey = new Map(groups.map((group) => [group.key, group]));
    const bySlug = new Map(groups.map((group) => [group.slug, group]));
    const aliasTargets = new Map();
    for (const group of groups) {
        for (const modelId of group.modelIds) {
            for (const candidate of [generateModelSlug(modelId), ...legacyModelSlugs(modelId)]) {
                if (!candidate || bySlug.has(candidate) || aliasTargets.has(candidate))
                    continue;
                aliasTargets.set(candidate, group);
                group.aliases.push(candidate);
            }
        }
        group.aliases.sort();
    }
    const byRawModelId = new Map();
    for (const group of groups) {
        for (const modelId of group.modelIds)
            byRawModelId.set(modelId, group);
    }
    return {
        groups,
        forModel(modelId, publisher) {
            return byKey.get(canonicalModelKey(modelId, publisher));
        },
        resolve(value) {
            if (!value)
                return undefined;
            return (bySlug.get(value) ??
                aliasTargets.get(value) ??
                byRawModelId.get(value) ??
                bySlug.get(modelPathSegmentToSlug(value)) ??
                aliasTargets.get(modelPathSegmentToSlug(value)));
        },
        redirects() {
            const map = {};
            for (const [alias, group] of [...aliasTargets.entries()].sort(([a], [b]) => a.localeCompare(b))) {
                map[alias] = group.slug;
            }
            return map;
        },
    };
}
//# sourceMappingURL=modelIdentity.js.map